import React, { useEffect } from "react";
import config from "../../config/config";
import { AdSenseProvider as AdSenseProviderImpl } from "./providers/adsense/AdSenseProvider";
import { EzoicProvider as EzoicProviderImpl } from "./providers/ezoic/EzoicProvider";
import { useSubscriptionPermissions } from "../../features/subscription/hooks/useSubscriptionData";
import { isRouteExcluded, getExclusionReason } from "./adConfig";

/**
 * Unified Ad Provider that loads the correct ad provider based on configuration
 * Only one provider is loaded at a time to ensure policy compliance
 * 
 * Ad display logic:
 * 1. Subscription permissions (adsHidden) - core business logic
 * 2. Route-based exclusions - additional restrictions for specific pages
 * 
 * Ads are shown only when BOTH conditions are met:
 * - User subscription allows ads (adsHidden !== true)
 * - Current route is not in the exclusion list
 */
export const AdProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const provider = config.ads.provider;

  // Read subscription permissions (hook must be called unconditionally)
  const { permissions } = useSubscriptionPermissions();

  // CORE BUSINESS LOGIC - UNCHANGED
  // Determine whether ads are allowed for the current user.
  // Ads are shown by default (for unauthenticated users, during loading, or
  // when adsHidden is false). They are only hidden when adsHidden is
  // explicitly true (e.g., premium subscribers).
  const allowAds = permissions?.adsHidden !== true;

  // Route-based exclusion using window.location (no Router context needed)
  // This prevents AdSense scripts from loading on excluded routes
  const routeExcluded = isRouteExcluded(window.location.pathname);
  
  // Final decision: BOTH subscription permissions AND route rules must allow ads
  const finalAllowAds = allowAds && !routeExcluded;

  // Add small dev-time helper to allow toggling mock permissions without reloading
  React.useEffect(() => {
    // Only attach the helper in development
    if (process.env.NODE_ENV !== 'development') return;

    // Expose a helper so developers can toggle subscription permission mock quickly
    // Example usage in the browser console:
    // window.__setMockSubscriptionPermissions({ adsHidden: true })
    // window.__setMockSubscriptionPermissions({ adsHidden: false })
    // The helper dispatches an event `mock-subscription-permissions-changed` to force a re-evaluation in AdProvider
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (window as any).__setMockSubscriptionPermissions = (perms: any) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      (window as any).__mock_subscription_permissions = perms;
      window.dispatchEvent(new Event('mock-subscription-permissions-changed'));
    };

    return () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      delete (window as any).__setMockSubscriptionPermissions;
    };
  }, []);

  // Force re-render when a dev-level mock subscription changes without reload
  const [, setForceTick] = React.useState(0);
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const handler = () => setForceTick((v) => v + 1);
    window.addEventListener('mock-subscription-permissions-changed', handler);
    return () => window.removeEventListener('mock-subscription-permissions-changed', handler);
  }, []);

  // Dev-time debug: log computed permissions/allowAds so we can trace why ads
  // might be loading in specific sessions. Use console.debug to keep output
  // quieter in normal environments.
  useEffect(() => {
    try {
      const exclusionReason = routeExcluded ? getExclusionReason(window.location.pathname) : null;
      console.debug("AdProvider: computed permissions=", permissions, "allowAds=", allowAds, "routeExcluded=", routeExcluded, "finalAllowAds=", finalAllowAds, "provider=", provider);
      // Explicit info trace so we can grep it in the console: single-line status to make detection easy
      if (finalAllowAds) {
        console.info(`AD: ENABLED provider=${provider} (adsHidden=${permissions?.adsHidden})`);
      } else {
        const reason = !allowAds ? 'subscription' : 'route';
        const details = !allowAds ? `adsHidden=${permissions?.adsHidden}` : `route=${window.location.pathname} reason="${exclusionReason}"`;
        console.info(`AD: DISABLED provider=${provider} reason=${reason} (${details})`);
      }
    } catch (e) {
      // swallow any logging errors in older browsers
    }
  }, [permissions, allowAds, routeExcluded, finalAllowAds, provider]);

  // Load AdSense provider
  if (provider === "adsense") {
    return <AdSenseProviderImpl allowAds={finalAllowAds}>{children}</AdSenseProviderImpl>;
  }

  // Load Ezoic provider
  if (provider === "ezoic") {
    return <EzoicProviderImpl allowAds={finalAllowAds}>{children}</EzoicProviderImpl>;
  }

  // Provider is 'none' or invalid - no ads
  return <>{children}</>;
};

// Re-export hooks for backward compatibility
export { useAdSense } from "./providers/adsense/AdSenseProvider";
export { useEzoic } from "./providers/ezoic/EzoicProvider";

// Export for backward compatibility (deprecated, use AdProvider instead)
export { AdSenseProvider as AdSenseProviderLegacy } from "./providers/adsense/AdSenseProvider";
